import { Component, OnInit } from '@angular/core';
import { NzModalService } from 'ng-zorro-antd/modal';
import { DataserviceService } from '../dataservice.service';
import { NzMessageService } from 'ng-zorro-antd/message';
import { AppComponent } from '../app.component';

type NzSortOrder = 'ascend' | 'descend' | null;

@Component({
  selector: 'app-processing-fee-queue',
  templateUrl: './processing-fee-queue.component.html',
  styleUrl: './processing-fee-queue.component.css'
})
export class ProcessingFeeQueueComponent implements OnInit {
  isLoading = false;
  loadingMessage = '';
  currentUser: any;
  processingFees: any[] = [];
  allProcessingFees: any[] = [];
  pageSize = 100;
  pageIndex = 1;
  total = 0;

  sortField = 'processing_fee_date';
  sortOrder: NzSortOrder = 'ascend';

  statusOptions: string[] = ['pending', 'complete', 'canceled', 'cancelled', 'chargeback', 'active'];
  statusFilter: string[] = [];
  repFilter = '';
  feeDateRange: Date[] | null = null;

  chargeOpen = false;
  editOpen = false;
  waiveOpen = false;
  selected: any = null;
  cardOptions: any[] = [];
  selectedCardId: string | null = null;
  selectedIndex: number | null = null;
  editedProcessingFee = '';

  constructor(
    private data: DataserviceService,
    private message: NzMessageService,
    private app: AppComponent,
    private modal: NzModalService
  ) {
    this.currentUser = JSON.parse(localStorage.getItem('user')!);
  }

  ngOnInit() {
    if (!this.feeDateRange) {
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - 30);
      this.feeDateRange = [startDate, endDate];
    }
    this.getData();
  }

  loadingHelper(onOff: boolean, message?: any) {
    this.loadingMessage = message ? message : 'Loading...';
    this.isLoading = onOff;
  }

  getData() {
    this.loadingHelper(true, 'Getting fees...');
    this.data.getData('work-order/processing-fee').subscribe(
      (res: any) => {
        if (res.result === 'success') {
          this.allProcessingFees = Array.isArray(res.data) ? res.data : [];
          this.applyFilters();
        } else {
          this.allProcessingFees = [];
          this.processingFees = [];
          this.message.error('Failed to load work orders.');
          this.total = 0;
        }
        this.loadingHelper(false);
      },
      () => {
        this.loadingHelper(false);
        this.message.error('Failed to load work orders.');
      }
    );
  }

  clearFilters() {
    this.statusFilter = [];
    this.repFilter = '';
    this.feeDateRange = null;
    this.pageIndex = 1;
    this.applyFilters();
  }

  showChargeModal(lead: any) {
    this.selected = lead;
    this.editedProcessingFee = this.normalizeFee(lead?.processing_fee);

    this.cardOptions = Array.isArray(lead?.cards) ? lead.cards : [];

    if (!this.cardOptions.length && Array.isArray(lead?.generic?.cards)) {
      this.cardOptions = lead.generic.cards;
    }

    if (this.cardOptions.length) {
      const defaultCard = this.cardOptions[0];
      this.selectedCardId = defaultCard?.authorizePaymentId || null;
    } else {
      const fallback = lead?.authorizePaymentId;
      if (Array.isArray(fallback)) {
        this.selectedCardId = fallback[0] || null;
      } else if (fallback) {
        this.selectedCardId = fallback;
      } else {
        this.selectedCardId = null;
      }

      if (this.selectedCardId) {
        this.cardOptions = [{
          authorizePaymentId: String(this.selectedCardId),
          authorizeCustomerId: lead?.authorizeCustomerId || lead?.generic?.authorizeCustomerId || null,
          cardLast4: lead?.generic?.cardLast4 || null,
        }];
      }
    }

    this.selectedIndex = this.computeSelectedCardIndex();
    this.chargeOpen = true;
  }

  showEditModal(lead: any) {
    this.selected = lead;
    this.editedProcessingFee = this.normalizeFee(lead?.processing_fee);
    this.editOpen = true;
  }

  closeCharge() {
    this.chargeOpen = false;
    this.selected = null;
    this.cardOptions = [];
    this.selectedCardId = null;
    this.selectedIndex = null;
    this.editedProcessingFee = '';
  }

  closeEdit() {
    this.editOpen = false;
    this.selected = null;
    this.editedProcessingFee = '';
  }

  private computeSelectedCardIndex(): number | null {
    if (!this.cardOptions?.length || this.selectedCardId == null) return null;
    const idx = this.cardOptions.findIndex(
      c => String(c?.authorizePaymentId) === String(this.selectedCardId)
    );
    return idx >= 0 ? idx : null;
  }

  onCardSelection(_val: any) {
    this.selectedIndex = this.computeSelectedCardIndex();
  }

  confirmCharge() {
    if (!this.selected) return;

    const amount = this.parseAmount(this.editedProcessingFee);
    if (amount < 0) {
      this.message.error('Enter a valid processing fee amount.');
      return;
    }

    const formatted = this.formatCurrency(amount);
    const customerName = `${this.selected.first_name || ''} ${this.selected.last_name || ''}`.trim() || 'this customer';

    this.modal.confirm({
      nzTitle: 'Charge processing fee?',
      nzContent: `Charge ${formatted} for ${customerName}? This will update the fee amount before charging.`,
      nzOkText: 'Yes, charge',
      nzCancelText: 'Cancel',
      nzOnOk: () => this.submitResolve(amount)
    });
  }

  private submitResolve(amount?: number) {
    if (!this.selected) return;

    const feeAmount = amount ?? this.parseAmount(this.editedProcessingFee);
    if (feeAmount < 0) {
      this.message.error('Enter a valid processing fee amount.');
      return;
    }

    const selectedCard = this.cardOptions.find(
      card => String(card?.authorizePaymentId) === String(this.selectedCardId)
    );
    const customerProfileId = selectedCard?.authorizeCustomerId
      || this.selected.authorizeCustomerId
      || this.selected.customerProfileId;

    let paymentProfileId: any = selectedCard?.authorizePaymentId
      || this.selectedCardId
      || this.selected.authorizePaymentId
      || this.selected.paymentProfileId;

    if (Array.isArray(paymentProfileId)) paymentProfileId = paymentProfileId[0];

    if (!feeAmount || !customerProfileId || !paymentProfileId) {
      this.message.error('Missing payment details for this processing fee.');
      return;
    }

    const payload = {
      customerId: this.selected.customer_id || this.selected.customerId,
      amount: feeAmount.toString(),
      authorizeCustomerId: String(customerProfileId),
      authorizePaymentId: String(paymentProfileId),
      index: this.selectedIndex
    };

    this.loadingHelper(true, 'Updating fee and charging...');

    this.updateProcessingFee(feeAmount, 'updated before charge').subscribe(
      () => this.chargeProcessingFee(payload),
      () => {
        this.loadingHelper(false);
        this.message.error('Failed to update processing fee amount.');
      }
    );
  }

  private chargeProcessingFee(payload: any) {
    this.data.postData({ ...payload }, 'dish/authorizeCharge').subscribe(
      (res: any) => {
        console.log(res)
        this.loadingHelper(false);

        const ok = res?.result === 'success';

        if (ok) {
          this.message.success('Processing fee charged successfully.');
          this.closeCharge();
          this.getData();
        } else {
          const error = res?.message || 'Unable to charge processing fee.';
          this.message.error(error);
        }
      },
      () => {
        this.loadingHelper(false);
        this.message.error('Failed to charge processing fee.');
      }
    );
  }

  showWaiveModal(lead: any) {
    this.selected = lead;
    this.waiveOpen = true;
  }

  closeWaive() {
    this.waiveOpen = false;
    this.selected = null;
  }

  submitWaive() {
    if (!this.selected || !this.selected.id) {
      this.message.error('Missing work order information.');
      return;
    }

    const payload = {
      id: this.selected.id
    };

    this.loadingHelper(true, 'Waiving processing fee...');
    this.data.postData(payload, 'work-order/waive-processing-fee').subscribe(
      (res: any) => {
        this.loadingHelper(false);

        const ok = res?.result === 'success';

        if (ok) {
          this.message.success('Processing fee waived successfully.');
          this.closeWaive();
          this.getData();
        } else {
          const error = res?.message || 'Unable to waive processing fee.';
          this.message.error(error);
        }
      },
      () => {
        this.loadingHelper(false);
        this.message.error('Failed to waive processing fee.');
      }
    );
  }

  onDateRangeChange(range: Date[] | null) {
    this.feeDateRange = Array.isArray(range) && range.length === 2 ? range : null;
    this.applyFilters();
  }

  setFee(amount: number) {
    this.editedProcessingFee = this.formatCurrency(amount);
  }

  isFeeSelected(amount: number): boolean {
    return this.parseAmount(this.editedProcessingFee) === Number(amount.toFixed(2));
  }

  private applyFilters() {
    const filtered = (this.allProcessingFees || []).filter(fee => this.isWithinSelectedRange(fee));
    this.processingFees = filtered;
    this.total = filtered.length;
  }

  private isWithinSelectedRange(fee: any): boolean {
    if (!this.feeDateRange || this.feeDateRange.length !== 2) {
      const feeDate = new Date(fee?.processing_fee_date);
      if (isNaN(feeDate.getTime())) return false;

      const today = new Date();
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(today.getDate() - 30);

      return feeDate >= thirtyDaysAgo && feeDate <= today;
    }

    const feeDateStr = this.toDateKey(fee?.processing_fee_date);
    const startStr = this.toDateKey(this.feeDateRange[0]);
    const endStr = this.toDateKey(this.feeDateRange[1]);

    if (!feeDateStr || !startStr || !endStr) return false;

    return feeDateStr >= startStr && feeDateStr <= endStr;
  }

  // Normalize a date or date-like string into a yyyy-MM-dd key without timezone drift.
  private toDateKey(value: any): string | null {
    if (!value) return null;

    // Handle plain yyyy-MM-dd strings explicitly to avoid timezone shifts.
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value.trim())) {
      return value.trim();
    }

    // Fallback: parse as Date and use UTC parts to avoid local offset shifts.
    const date = value instanceof Date ? value : new Date(value);
    if (isNaN(date.getTime())) return null;

    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const day = String(date.getUTCDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  downloadCsv() {
    const rows = this.processingFees || [];
    if (!rows.length) {
      this.message.warning('No processing fees to export for the selected range.');
      return;
    }

    const header = [
      'Customer',
      'Processing Fee',
      'Fee Date',
      'Last Attempt Result',
      'Last Attempt Date',
      'Last Attempt Rep',
      'Customer ID',
    ];

    const csvRows = rows.map((fee: any) => {
      const lastAttempt = this.getLastAttempt(fee);
      const lastAttemptDate = lastAttempt?.dateAttempted ? this.formatDateForCsv(lastAttempt.dateAttempted) : '';

      const values = [
        `${fee.first_name || ''} ${fee.last_name || ''}`.trim(),
        fee.processing_fee || '',
        this.formatDateForCsv(fee.processing_fee_date),
        lastAttempt?.result || '',
        lastAttemptDate,
        lastAttempt?.repName || '',
        fee.customer_crm_id || '',
      ];

      return values.map(v => `"${String(v ?? '').replace(/"/g, '""')}"`).join(',');
    });

    const csv = [header.join(','), ...csvRows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `processing-fees-${this.getRangeLabel()}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);

    this.message.success('Download started.');
  }

  private getLastAttempt(fee: any) {
    const attempts = Array.isArray(fee?.attempts) ? fee.attempts : [];
    if (!attempts.length) return null;
    return attempts[attempts.length - 1];
  }

  private formatDateForCsv(dateValue: any): string {
    const date = new Date(dateValue);
    if (isNaN(date.getTime())) return '';
    return date.toLocaleDateString();
  }

  private getRangeLabel(): string {
    if (!this.feeDateRange || this.feeDateRange.length !== 2) return 'all';
    const [start, end] = this.feeDateRange;
    return `${this.formatForFilename(start)}-${this.formatForFilename(end)}`;
  }

  private formatForFilename(date: Date): string {
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    return `${yyyy}${mm}${dd}`;
  }

  onFeeBlur() {
    const amount = this.parseAmount(this.editedProcessingFee);
    this.editedProcessingFee = this.formatCurrency(amount);
  }

  private parseAmount(val: any): number {
    if (val == null) return 0;
    const num = Number(String(val).replace(/[^0-9.]/g, ''));
    return isNaN(num) ? 0 : Number(num.toFixed(2));
  }

  private formatCurrency(val: number): string {
    return `$${val.toFixed(2)}`;
  }

  private normalizeFee(val: any): string {
    const num = this.parseAmount(val);
    return num ? this.formatCurrency(num) : '';
  }

  private updateProcessingFee(amount: number, resultLabel: string = 'manual edit') {
    const payload = {
      id: this.selected?.id,
      processingFee: this.formatCurrency(amount),
      amount: this.formatCurrency(amount),
      dateAttempted: new Date().toISOString(),
      result: resultLabel,
      userId: this.currentUser?.id,
      repName: this.currentUser?.first_name && this.currentUser?.last_name
        ? `${this.currentUser.first_name} ${this.currentUser.last_name}`
        : this.currentUser?.first_name || ''
    };

    return this.data.postDataNoBodyDotBody(payload, 'work-order/updatePfee');
  }

  saveFeeOnly() {
    if (!this.selected) return;
    const feeAmount = this.parseAmount(this.editedProcessingFee);
    if (feeAmount < 0) {
      this.message.error('Enter a valid processing fee amount.');
      return;
    }
    this.loadingHelper(true, 'Updating processing fee...');
    this.updateProcessingFee(feeAmount, 'manual edit').subscribe(
      () => {
        this.loadingHelper(false);
        this.message.success('Processing fee updated.');
        this.updateLocalFeeDisplay(feeAmount);
        this.closeEdit();
      },
      () => {
        this.loadingHelper(false);
        this.message.error('Failed to update processing fee.');
      }
    );
  }

  private updateLocalFeeDisplay(amount: number) {
    const formatted = this.formatCurrency(amount);
    if (this.selected) {
      this.selected.processing_fee = formatted;
    }
    const updateRow = (row: any) => row?.id === this.selected?.id ? { ...row, processing_fee: formatted } : row;
    this.allProcessingFees = this.allProcessingFees.map(updateRow);
    this.processingFees = this.processingFees.map(updateRow);
  }
}
